<script setup lang="ts">
import type { Report } from '@functions/types/analysis';
import { format } from 'date-fns';
import { collection, orderBy, query } from 'firebase/firestore';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCollection } from 'vuefire';
import { useDebugUser } from '@/composables/useDebugUser';
import { db } from '@/firebase';
import Container from '@/layouts/Container.vue';

definePage({
  name: 'reports',
});

const router = useRouter();
const user = useDebugUser();

// Filter states
const sortBy = ref('date-desc');
const rows = ref(10);
const first = ref(0);

// Helper function to get text color based on score
function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  return 'text-red-600';
}

// Computed query based on filters
const reportsQuery = computed(() => {
  if (!user.value?.uid) return null;

  const collectionRef = collection(db, 'imports', user.value.uid, 'reports');
  const constraints = [];

  // Add sorting
  const [sortDirection] = sortBy.value.split('-');
  constraints.push(orderBy('createdAt', sortDirection === 'desc' ? 'desc' : 'asc'));

  return query(collectionRef, ...constraints);
});

// Fetch reports using VueFire
const { data: reports, pending } = useCollection<Report>(reportsQuery);

function viewReport(reportId: string) {
  router.push(`/reports/${reportId}`);
}
</script>

<template>
  <Container>
    <template #title>
      AI Reports
    </template>
    <div id="reports-table" class="bg-white rounded-lg p-4 shadow-md">
      <h2 class="text-xl font-semibold mb-4 text-gray-900">
        Reports
      </h2>

      <div v-if="pending" class="text-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl text-gray-900" />
        <p class="mt-2 text-gray-900">
          Loading reports...
        </p>
      </div>

      <div v-else-if="reports?.length === 0" class="text-center text-gray-900 py-8">
        No reports found. Upload a session to generate your first AI report.
      </div>

      <DataTable
        v-else
        v-model:rows="rows"
        v-model:first="first"
        :value="reports"
        :paginator="true"
        :rows-per-page-options="[10, 20, 50]"
        responsive-layout="scroll"
        :sort-order="sortBy === 'date-asc' ? 1 : -1"
        sort-field="recordedAt"
      >
        <Column
          sortable
          field="recordedAt"
          header="Recorded At"
          style="width: 20%"
        >
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="text-gray-900">{{ format(data.recordedAt.toDate(), 'MMM d, yyyy') }}</span>
              <span class="text-gray-900 text-sm">{{ format(data.recordedAt.toDate(), 'h:mm a') }}</span>
            </div>
          </template>
        </Column>
        <Column
          sortable
          field="createdAt"
          header="Generated At"
          style="width: 20%"
        >
          <template #body="{ data }">
            <div class="flex flex-col">
              <span class="text-gray-900">{{ format(data.createdAt.toDate(), 'MMM d, yyyy') }}</span>
              <span class="text-gray-900 text-sm">{{ format(data.createdAt.toDate(), 'h:mm a') }}</span>
            </div>
          </template>
        </Column>
        <Column
          sortable
          field="shotCount"
          header="Shot Count"
          style="width: 15%"
        >
          <template #body="{ data }">
            <span class="text-gray-900">{{ data.shotCount || 0 }}</span>
          </template>
        </Column>
        <Column
          sortable
          field="analysis.performanceMetrics.overallScore"
          header="Overall Score"
          style="width: 15%"
        >
          <template #body="{ data }">
            <span :class="getScoreColor(data.analysis?.performanceMetrics?.overallScore || 0)" class="font-semibold">
              {{ data.analysis?.performanceMetrics?.overallScore?.toFixed(1) || 'N/A' }}
            </span>
          </template>
        </Column>
        <Column style="width: 10%" header="View Report">
          <template #body="{ data }">
            <Button
              icon="pi pi-chart-bar"
              class="p-button-sm p-button-rounded p-button-secondary"
              @click="viewReport(data.id)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </Container>
</template>

<style>
#reports-table {
  --p-paginator-background: white;
  --p-datatable-header-cell-background: white;
  --p-datatable-header-cell-color: var(--color-gray-900);
  --p-datatable-header-cell-selected-color: var(--color-gray-900);
  --p-datatable-row-background: white;
  --p-datatable-row-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-background: var(--color-gray-50);
  --p-datatable-row-selected-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-hover-background: var(--color-gray-50);
  --p-datatable-row-selected-focus-hover-background: var(--color-gray-50);
  --p-paginator-nav-button-selected-color: var(--color-gray-900);
}
</style>
